import type {
  PlannerServiceSpan,
  PlannerTransportVehicle,
} from '@travel-planner/shared/utils/planner-day-services';
import type {
  PlannerTransferAssistance,
  PlannerTransferKind,
  PlannerTransferSpot,
  PlannerTransferTime,
} from '@travel-planner/shared/utils/planner-transfers';
import type {
  AdvisorDraft,
  AdvisorDraftDay,
  AdvisorDraftItem,
  AdvisorDraftLeg,
  AdvisorGuests,
  AdvisorHotelBooking,
  AdvisorProduct,
} from './types';
import { arrayMove } from '@dnd-kit/sortable';
import {
  clockToMinutes,
  minutesToClock,
  sortPlannerDay,
  withPlannerDayClock,
} from '@travel-planner/shared/utils/planner-day-clock';
import { tourNeedsCar } from '@travel-planner/shared/utils/planner-day-services';
import { isDepartureDay, nightDays } from './departure-day';

const nextId = (prefix: string): string =>
  `${prefix}-${Math.random().toString(36).slice(2, 10)}`;

/** The product's usual start (its first leg's), for anything but a hotel. */
export const usualStartTime = (
  product: Pick<AdvisorProduct, 'kind' | 'startMinutes'>
): string | null =>
  product.kind !== 'hotel' &&
  typeof product.startMinutes === 'number' &&
  product.startMinutes >= 0 &&
  product.startMinutes < 24 * 60
    ? minutesToClock(product.startMinutes)
    : null;

const toItem = (product: AdvisorProduct): AdvisorDraftItem => ({
  id: nextId('item'),
  productId: product.id,
  title: product.title,
  kind: product.kind,
  clientPrice: product.clientPrice,
  childPrice: product.childPrice ?? null,
  currency: product.currency,
  subtitle: product.subtitle,
  thumbnail: product.thumbnail,
  origin: 'manual',
  startTime: usualStartTime(product),
  ...(product.location ? { location: product.location } : {}),
  ...(product.pricingType ? { pricingType: product.pricingType } : {}),
  ...(product.durationMinutes
    ? { durationMinutes: product.durationMinutes }
    : {}),
  avgRoomNight: product.avgRoomNight ?? null,
});

/** Adds a night day at the chapter's end, before the departure day. */
const pushNight = (leg: AdvisorDraftLeg, day: AdvisorDraftDay): void => {
  const at = leg.days.findIndex(isDepartureDay);
  if (at === -1) leg.days.push(day);
  else leg.days.splice(at, 0, day);
};

const emptyDay = (items: AdvisorDraftItem[] = []): AdvisorDraftDay => ({
  id: nextId('day'),
  items,
});

const emptyLeg = (
  regionId: string,
  regionTitle: string,
  country: string | null = null
): AdvisorDraftLeg => ({
  id: nextId('leg'),
  regionId,
  regionTitle,
  ...(country ? { country } : {}),
  days: [],
});

const copyLegs = (draft: AdvisorDraft): AdvisorDraftLeg[] =>
  draft.legs.map((leg) => ({
    ...leg,
    days: leg.days.map((day) => ({ ...day, items: [...day.items] })),
  }));

/** The chapter of the product's region — matched by region id, never by name. */
export const findChapter = (
  legs: AdvisorDraftLeg[],
  product: Pick<AdvisorProduct, 'regionId'>
): AdvisorDraftLeg | undefined =>
  product.regionId
    ? legs.find((entry) => entry.regionId === product.regionId)
    : undefined;

/**
 * A product goes only into its own region's chapter: a Lisbon activity never
 * joins a Madrid day. A product with no region yet can go anywhere.
 */
export const fitsChapter = (
  product: Pick<AdvisorProduct, 'regionId'>,
  leg: Pick<AdvisorDraftLeg, 'regionId'>
): boolean => !product.regionId || leg.regionId === product.regionId;

const chapterOfDay = (
  draft: AdvisorDraft,
  dayId: string
): AdvisorDraftLeg | undefined =>
  draft.legs.find((leg) => leg.days.some((day) => day.id === dayId));

/**
 * Why a product can't go on a day — the day is in another region — or null
 * when it can. Shown to the agent instead of a silent refusal.
 */
export const dayRegionRefusal = (
  draft: AdvisorDraft,
  product: Pick<AdvisorProduct, 'title' | 'regionId' | 'regionTitle'>,
  dayId: string,
  displayTitle: (title: string) => string = (title) => title
): string | null => {
  const leg = chapterOfDay(draft, dayId);
  if (!leg || fitsChapter(product, leg)) return null;
  const region = displayTitle(product.regionTitle || product.regionId || '');
  return `${product.title} is in ${region}, so it can only go on ${region} days.`;
};

/** The same for an item already on the rail: it stays in its chapter's region. */
export const itemRegionRefusal = (
  draft: AdvisorDraft,
  itemId: string,
  dayId: string,
  displayTitle: (title: string) => string = (title) => title
): string | null => {
  for (const leg of draft.legs) {
    for (const day of leg.days) {
      const item = day.items.find((entry) => entry.id === itemId);
      if (!item) continue;
      return dayRegionRefusal(
        draft,
        {
          title: item.title,
          regionId: leg.regionId,
          regionTitle: leg.regionTitle,
        },
        dayId,
        displayTitle
      );
    }
  }
  return null;
};

/**
 * The product's region chapter, added when the trip has none yet. A product
 * with no region has none: it cannot open a chapter.
 */
const chapterFor = (
  legs: AdvisorDraftLeg[],
  product: AdvisorProduct
): AdvisorDraftLeg | undefined => {
  if (!product.regionId) return undefined;
  const found = findChapter(legs, product);
  if (found) {
    if (!found.country && product.country) found.country = product.country;
    return found;
  }
  const leg = emptyLeg(
    product.regionId,
    product.regionTitle || product.regionId,
    product.country ?? null
  );
  legs.push(leg);
  return leg;
};
const dayHasHotel = (day: AdvisorDraftDay): boolean =>
  day.items.some((item) => item.kind === 'hotel');

/** Default kind order: hotel, food, experiences, remaining food. */
const kindOrder = (items: AdvisorDraftItem[]): AdvisorDraftItem[] => {
  const hotels = items.filter((item) => item.kind === 'hotel');
  const restaurants = items.filter((item) => item.kind === 'restaurant');
  const experiences = items.filter((item) => item.kind === 'experience');
  const other = items.filter(
    (item) =>
      item.kind !== 'hotel' &&
      item.kind !== 'restaurant' &&
      item.kind !== 'experience'
  );
  return [
    ...hotels,
    ...restaurants.slice(0, 1),
    ...experiences,
    ...restaurants.slice(1),
    ...other,
  ];
};

/**
 * The day as the rail shows it: every item given a time (`withPlannerDayClock`)
 * and ordered by it, the hotel first.
 */
export const orderDayItems = (items: AdvisorDraftItem[]): AdvisorDraftItem[] =>
  sortPlannerDay(withPlannerDayClock(kindOrder(items)));

export const createEmptyDraft = (
  title = 'Untitled proposal'
): AdvisorDraft => ({
  title,
  reference: `TV-${String(Date.now()).slice(-4)}`,
  legs: [],
});

export const addProductToDraft = (
  draft: AdvisorDraft,
  product: AdvisorProduct
): AdvisorDraft => {
  const legs = copyLegs(draft);
  // No region: the trip's last chapter takes it (nothing when there is none).
  const leg = chapterFor(legs, product) ?? legs[legs.length - 1];
  if (!leg) return draft;
  const item = toItem(product);
  const nights = nightDays(leg);
  const lastDay = nights[nights.length - 1];
  // A day is one night. Hotel opens a new day only when the last day
  // already has a bed; an empty "+ Add day" slot or a restaurant-only
  // day still takes the hotel. Everything else lands on the last night.
  if (!lastDay || (product.kind === 'hotel' && dayHasHotel(lastDay))) {
    pushNight(leg, emptyDay([item]));
  } else {
    lastDay.items.push(item);
    lastDay.items = orderDayItems(lastDay.items);
  }
  return { ...draft, legs };
};

/** A new day at the end of the product's chapter (added when missing). */
export const addProductToNewDay = (
  draft: AdvisorDraft,
  product: AdvisorProduct
): AdvisorDraft => {
  const legs = copyLegs(draft);
  const leg = chapterFor(legs, product);
  if (!leg) return draft;
  pushNight(leg, emptyDay(orderDayItems([toItem(product)])));
  return { ...draft, legs };
};

export const addProductToDay = (
  draft: AdvisorDraft,
  product: AdvisorProduct,
  dayId: string
): AdvisorDraft => {
  const leg = draft.legs.find((entry) =>
    entry.days.some((day) => day.id === dayId)
  );
  if (!leg) return addProductToDraft(draft, product);
  if (!fitsChapter(product, leg)) return draft;
  const item = toItem(product);
  return {
    ...draft,
    legs: draft.legs.map((leg) => ({
      ...leg,
      days: leg.days.map((day) =>
        day.id === dayId
          ? { ...day, items: orderDayItems([...day.items, item]) }
          : day
      ),
    })),
  };
};

export const addEmptyDay = (
  draft: AdvisorDraft,
  legId: string
): AdvisorDraft => ({
  ...draft,
  legs: draft.legs.map((leg) => {
    if (leg.id !== legId) return leg;
    const next = { ...leg, days: [...leg.days] };
    pushNight(next, emptyDay());
    return next;
  }),
});

/** The most nights a base hotel can be set for at once. */
export const MAX_CHAPTER_NIGHTS = 30;

/**
 * A region chapter's nights now, and the fewest it can be set to: a day
 * holding items is never dropped, so not below the last one. A region the
 * trip has no chapter for starts at one night.
 */
/**
 * The nights a new region chapter starts with: the brief's on a trip with no
 * days yet, else one.
 */
export const newChapterNights = (
  draft: AdvisorDraft,
  briefNights: number | null | undefined
): number =>
  draft.legs.every((leg) => nightDays(leg).length === 0) && briefNights
    ? Math.max(1, Math.round(briefNights))
    : 1;

export const chapterStay = (
  draft: AdvisorDraft,
  regionId: string | null | undefined,
  briefNights?: number | null
): { nights: number; minNights: number } => {
  const leg = regionId
    ? draft.legs.find((entry) => entry.regionId === regionId)
    : undefined;
  if (!leg) {
    return { nights: newChapterNights(draft, briefNights), minNights: 1 };
  }
  const lastUsed = nightDays(leg).reduce(
    (last, day, index) => (day.items.length > 0 ? index + 1 : last),
    0
  );
  return { nights: chapterNights(leg), minNights: Math.max(1, lastUsed) };
};

/**
 * Sets the chapter's base hotel, adding the chapter for the hotel's region
 * when the trip has none yet. Replaces any earlier one. With `nights` the
 * chapter gets that many days (one per night): empty days are added at the
 * end, or its empty last days dropped — never a day holding items.
 */
export const setBaseHotel = (
  draft: AdvisorDraft,
  product: AdvisorProduct,
  nights?: number
): AdvisorDraft => {
  const legs = copyLegs(draft);
  const leg = chapterFor(legs, product);
  if (!leg) return draft;
  leg.baseHotel = { ...toItem(product), kind: 'hotel' };
  const days = nightDays(leg);
  const departure = leg.days.filter(isDepartureDay);
  const target = Math.min(
    MAX_CHAPTER_NIGHTS,
    Math.max(1, Math.round(nights ?? days.length))
  );
  while (days.length < target) days.push(emptyDay());
  while (
    days.length > target &&
    (days[days.length - 1]?.items.length ?? 0) === 0
  ) {
    days.pop();
  }
  leg.days = [...days, ...departure];
  return { ...draft, legs };
};

/**
 * Removes a day with its items. A chapter keeps its last day; the chapter
 * itself is removed instead.
 */
export const removeDay = (draft: AdvisorDraft, dayId: string): AdvisorDraft => {
  const leg = draft.legs.find((entry) =>
    entry.days.some((day) => day.id === dayId)
  );
  // The departure day stays, and so does a chapter's last night.
  const day = leg?.days.find((entry) => entry.id === dayId);
  if (!leg || !day || isDepartureDay(day) || nightDays(leg).length <= 1) {
    return draft;
  }
  return {
    ...draft,
    legs: draft.legs.map((entry) =>
      entry === leg
        ? { ...entry, days: entry.days.filter((day) => day.id !== dayId) }
        : entry
    ),
  };
};

export const removeBaseHotel = (
  draft: AdvisorDraft,
  legId: string
): AdvisorDraft => ({
  ...draft,
  legs: draft.legs.map((leg) =>
    leg.id === legId ? { ...leg, baseHotel: null } : leg
  ),
});

/**
 * Hotels booked on a chapter's days (drafts saved before base hotels, or a
 * trip Generate): the first becomes the base hotel and its copies on the
 * other nights go, since the base hotel already covers every night. A
 * different hotel stays on its day.
 */
export const withBaseHotels = (draft: AdvisorDraft): AdvisorDraft => {
  let changed = false;
  const legs = draft.legs.map((leg) => {
    if (leg.baseHotel) return leg;
    const hotel = leg.days
      .flatMap((day) => day.items)
      .find((item) => item.kind === 'hotel');
    if (!hotel) return leg;
    changed = true;
    return {
      ...leg,
      baseHotel: hotel,
      days: leg.days.map((day) => ({
        ...day,
        items: day.items.filter(
          (item) =>
            !(item.kind === 'hotel' && item.productId === hotel.productId)
        ),
      })),
    };
  });
  return changed ? { ...draft, legs } : draft;
};

/**
 * Nights the base hotel covers: one per chapter day but the departure day,
 * at least one.
 */
export const chapterNights = (leg: AdvisorDraftLeg): number =>
  Math.max(1, nightDays(leg).length);

export const removeLeg = (
  draft: AdvisorDraft,
  legId: string
): AdvisorDraft => ({
  ...draft,
  legs: draft.legs.filter((leg) => leg.id !== legId),
});

export const removeItem = (
  draft: AdvisorDraft,
  itemId: string
): AdvisorDraft => ({
  ...draft,
  legs: draft.legs.map((leg) => ({
    ...leg,
    days: leg.days.map((day) => ({
      ...day,
      items: day.items.filter((item) => item.id !== itemId),
    })),
  })),
});

/** The agent's comment on a day item or base hotel; blank clears it. */
export const setItemComment = (
  draft: AdvisorDraft,
  itemId: string,
  comment: string
): AdvisorDraft => {
  const text = comment.trim() || null;
  const withComment = (item: AdvisorDraftItem): AdvisorDraftItem =>
    item.id === itemId ? { ...item, comment: text } : item;
  return {
    ...draft,
    legs: draft.legs.map((leg) => ({
      ...leg,
      baseHotel: leg.baseHotel ? withComment(leg.baseHotel) : leg.baseHotel,
      days: leg.days.map((day) => ({
        ...day,
        items: day.items.map(withComment),
      })),
    })),
  };
};

/** A base hotel's Send me a quote / Already booked (T-357). */
export const setHotelBooking = (
  draft: AdvisorDraft,
  itemId: string,
  booking: AdvisorHotelBooking
): AdvisorDraft => ({
  ...draft,
  legs: draft.legs.map((leg) =>
    leg.baseHotel?.id === itemId
      ? { ...leg, baseHotel: { ...leg.baseHotel, hotelBooking: booking } }
      : leg
  ),
});

/**
 * The travellers booked on a product's day items (XT-840); null books it for
 * the whole party again. A base hotel is always for everyone.
 */
export const setProductGuests = (
  draft: AdvisorDraft,
  productId: string,
  guests: AdvisorGuests | null
): AdvisorDraft => ({
  ...draft,
  legs: draft.legs.map((leg) => ({
    ...leg,
    days: leg.days.map((day) => ({
      ...day,
      items: day.items.map((item) =>
        item.productId === productId && item.kind !== 'hotel'
          ? { ...item, guests }
          : item
      ),
    })),
  })),
});

/**
 * Drops a day's Guide or Transport line; later changes keep it dropped. One
 * of several chosen cars drops just that car.
 */
export const removeDayService = (
  draft: AdvisorDraft,
  dayId: string,
  service: { kind: 'guide' | 'transport'; itemId: string; vehicle?: string }
): AdvisorDraft => ({
  ...draft,
  legs: draft.legs.map((leg) => ({
    ...leg,
    days: leg.days.map((day) => {
      if (day.id !== dayId) return day;
      if (service.kind === 'transport') {
        const remaining = dropOneVehicle(
          day.transportVehicles,
          service.vehicle
        );
        return remaining
          ? { ...day, transportVehicles: remaining }
          : {
              ...day,
              transportRemoved: true,
              transportVehicles: null,
              transportSpan: null,
            };
      }
      return {
        ...day,
        items: day.items.map((item) =>
          item.id === service.itemId ? { ...item, guideRemoved: true } : item
        ),
      };
    }),
  })),
});

const findItemDay = (
  draft: AdvisorDraft,
  itemId: string
): AdvisorDraftDay | undefined =>
  draft.legs
    .flatMap((leg) => leg.days)
    .find((day) => day.items.some((item) => item.id === itemId));

/** An item moved to start at `startTime`, its end moving with it. */
const startingAt = (
  item: AdvisorDraftItem,
  startTime: string
): AdvisorDraftItem => {
  if (!item.endTime || !item.startTime) return { ...item, startTime };
  const shift = clockToMinutes(startTime) - clockToMinutes(item.startTime);
  return {
    ...item,
    startTime,
    endTime: minutesToClock(clockToMinutes(item.endTime) + shift),
  };
};

/**
 * Items reordered on a day keep the day's times in place: each takes the
 * start of the position it lands in, so the order the agent dragged is the
 * order booked.
 */
const keepDayClocks = (items: AdvisorDraftItem[]): AdvisorDraftItem[] => {
  const clocks = items
    .flatMap((item) =>
      item.kind !== 'hotel' && item.startTime ? [item.startTime] : []
    )
    .sort((x, y) => clockToMinutes(x) - clockToMinutes(y));
  if (clocks.length === 0) return items;
  let next = 0;
  const placed = items.map((item) =>
    item.kind !== 'hotel' && item.startTime && next < clocks.length
      ? startingAt(item, clocks[next++] as string)
      : item
  );
  return sortPlannerDay(withPlannerDayClock(placed));
};

/**
 * Sets an item's time on its day; the day then follows the clock. An end
 * not after the start is dropped, so the item lasts its usual length.
 */
export const setItemTime = (
  draft: AdvisorDraft,
  itemId: string,
  startTime: string,
  endTime: string | null
): AdvisorDraft => {
  const day = findItemDay(draft, itemId);
  if (!day) return draft;
  const end =
    endTime && clockToMinutes(endTime) > clockToMinutes(startTime)
      ? endTime
      : null;
  return {
    ...draft,
    legs: draft.legs.map((leg) => ({
      ...leg,
      days: leg.days.map((entry) =>
        entry.id === day.id
          ? {
              ...entry,
              items: orderDayItems(
                entry.items.map((item) =>
                  item.id === itemId
                    ? {
                        ...item,
                        startTime,
                        endTime: end,
                        ...(end
                          ? {
                              durationMinutes:
                                clockToMinutes(end) - clockToMinutes(startTime),
                            }
                          : {}),
                      }
                    : item
                )
              ),
            }
          : entry
      ),
    })),
  };
};

/**
 * Drops the item onto another; in the same day it reorders, across days it
 * moves in front of the item it was dropped on.
 */
export const reorderDayItems = (
  draft: AdvisorDraft,
  activeItemId: string,
  overItemId: string
): AdvisorDraft => {
  if (activeItemId === overItemId) return draft;
  const from = findItemDay(draft, activeItemId);
  const to = findItemDay(draft, overItemId);
  if (!from || !to) return draft;
  if (from.id !== to.id) {
    return moveItemToDay(draft, activeItemId, to.id, overItemId);
  }
  return {
    ...draft,
    legs: draft.legs.map((leg) => ({
      ...leg,
      days: leg.days.map((day) => {
        if (day.id !== from.id) return day;
        const a = day.items.findIndex((item) => item.id === activeItemId);
        const b = day.items.findIndex((item) => item.id === overItemId);
        return { ...day, items: keepDayClocks(arrayMove(day.items, a, b)) };
      }),
    })),
  };
};

/** Moves an item to another day — at the end, or in front of `beforeItemId`. */
export const moveItemToDay = (
  draft: AdvisorDraft,
  itemId: string,
  dayId: string,
  beforeItemId: string | null = null
): AdvisorDraft => {
  const from = findItemDay(draft, itemId);
  const moving = from?.items.find((item) => item.id === itemId);
  const legOf = (id: string) =>
    draft.legs.find((leg) => leg.days.some((day) => day.id === id));
  const target = legOf(dayId);
  // An item stays in its region: it moves between that chapter's days only.
  if (!from || !moving || !target || legOf(from.id) !== target) return draft;
  return {
    ...draft,
    legs: draft.legs.map((leg) => ({
      ...leg,
      days: leg.days.map((day) => {
        const items = day.items.filter((item) => item.id !== itemId);
        if (day.id !== dayId) {
          return day.id === from.id ? { ...day, items } : day;
        }
        const at = beforeItemId
          ? items.findIndex((item) => item.id === beforeItemId)
          : -1;
        const next = [...items];
        next.splice(at < 0 ? next.length : at, 0, moving);
        // It keeps its time; the day follows the clock.
        return { ...day, items: orderDayItems(next) };
      }),
    })),
  };
};

export const draftItemCount = (draft: AdvisorDraft): number =>
  draft.legs.reduce(
    (count, leg) =>
      count +
      (leg.baseHotel ? 1 : 0) +
      leg.days.reduce((dayCount, day) => dayCount + day.items.length, 0),
    0
  );

export const draftDayCount = (draft: AdvisorDraft): number =>
  draft.legs.reduce((count, leg) => count + leg.days.length, 0);

/** The chosen cars less one of `vehicle`; null when none would be left. */
const dropOneVehicle = (
  vehicles: readonly PlannerTransportVehicle[] | null | undefined,
  vehicle: string | undefined
): PlannerTransportVehicle[] | null => {
  if (!vehicles || !vehicle) return null;
  let dropped = false;
  const remaining = vehicles.flatMap((entry) => {
    if (dropped || entry.type !== vehicle) return [entry];
    dropped = true;
    return entry.count > 1 ? [{ ...entry, count: entry.count - 1 }] : [];
  });
  return remaining.some((entry) => entry.count > 0) ? remaining : null;
};

/**
 * Sets the cars on a day's Transport (one line each) and brings a removed
 * Transport back; null or none: the suggested vehicle again.
 */
export const setDayTransportVehicles = (
  draft: AdvisorDraft,
  dayId: string,
  vehicles: readonly PlannerTransportVehicle[] | null
): AdvisorDraft => {
  const kept = (vehicles ?? []).filter((entry) => entry.count > 0);
  return {
    ...draft,
    legs: draft.legs.map((leg) => ({
      ...leg,
      days: leg.days.map((day) =>
        day.id === dayId
          ? {
              ...day,
              transportRemoved: false,
              transportVehicles: kept.length > 0 ? [...kept] : null,
            }
          : day
      ),
    })),
  };
};

/** The agent's half or full day for a day's Transport (T-326). */
export const setDayTransportSpan = (
  draft: AdvisorDraft,
  dayId: string,
  span: PlannerServiceSpan
): AdvisorDraft => ({
  ...draft,
  legs: draft.legs.map((leg) => ({
    ...leg,
    days: leg.days.map((day) =>
      day.id === dayId
        ? { ...day, transportRemoved: false, transportSpan: span }
        : day
    ),
  })),
});

/**
 * Brings back a Guide or a day's Transport the agent removed, or adds the
 * Transport a day whose experiences need no car does not suggest.
 */
export const restoreDayService = (
  draft: AdvisorDraft,
  dayId: string,
  service: { kind: 'guide' | 'transport'; itemId: string }
): AdvisorDraft => ({
  ...draft,
  legs: draft.legs.map((leg) => ({
    ...leg,
    days: leg.days.map((day) => {
      if (day.id !== dayId) return day;
      if (service.kind === 'transport') {
        // No experience needs the car: it comes back as a half day.
        const needed = day.items.some(
          (item) => item.kind === 'experience' && tourNeedsCar(item)
        );
        return {
          ...day,
          transportRemoved: false,
          transportSpan: needed ? day.transportSpan : 'half',
        };
      }
      return {
        ...day,
        items: day.items.map((item) =>
          item.id === service.itemId ? { ...item, guideRemoved: false } : item
        ),
      };
    }),
  })),
});

/** Drops or brings back one of the trip's transfers on its day (XT-920). */
export const setTransferRemoved = (
  draft: AdvisorDraft,
  dayId: string,
  kind: PlannerTransferKind,
  removed: boolean
): AdvisorDraft =>
  updateDay(draft, dayId, (day) => {
    const others = (day.transfersRemoved ?? []).filter(
      (entry) => entry !== kind
    );
    return { ...day, transfersRemoved: removed ? [...others, kind] : others };
  });

/**
 * Sets the cars, or a flight or train, on one of the day's transfers and brings it
 * back if removed; null or none: the vehicle that seats the party again.
 */
export const setTransferVehicles = (
  draft: AdvisorDraft,
  dayId: string,
  kind: PlannerTransferKind,
  vehicles: readonly PlannerTransportVehicle[] | null
): AdvisorDraft =>
  updateDay(draft, dayId, (day) => {
    const kept = (vehicles ?? []).filter((entry) => entry.count > 0);
    return {
      ...day,
      transfersRemoved: (day.transfersRemoved ?? []).filter(
        (entry) => entry !== kind
      ),
      transferVehicles: {
        ...day.transferVehicles,
        [kind]: kept.length > 0 ? kept : null,
      },
    };
  });

/**
 * Drops one car of a transfer the agent chose cars for; its last car, or a
 * transfer on the suggested vehicle, drops the transfer.
 */
export const removeTransferLine = (
  draft: AdvisorDraft,
  dayId: string,
  kind: PlannerTransferKind,
  vehicle?: string
): AdvisorDraft =>
  updateDay(draft, dayId, (day) => {
    const remaining = dropOneVehicle(
      day.transferVehicles?.[kind] ?? null,
      vehicle
    );
    if (remaining) {
      return {
        ...day,
        transferVehicles: { ...day.transferVehicles, [kind]: remaining },
      };
    }
    return {
      ...day,
      transfersRemoved: [
        ...(day.transfersRemoved ?? []).filter((entry) => entry !== kind),
        kind,
      ],
      transferVehicles: { ...day.transferVehicles, [kind]: null },
    };
  });

/**
 * Where the arrival starts or the departure ends: a kind of place, or one
 * picked on the map; null or blank: the airport. Its VIP assistance was the
 * old place's, so it goes (T-423).
 */
export const setTransferPlace = (
  draft: AdvisorDraft,
  dayId: string,
  kind: 'arrival' | 'departure',
  place: string | PlannerTransferSpot | null
): AdvisorDraft =>
  updateDay(draft, dayId, (day) => ({
    ...day,
    transferPlaces: {
      ...day.transferPlaces,
      [kind]: typeof place === 'string' ? place.trim() || null : place,
    },
    ...(day.transferAssistance?.[kind]
      ? { transferAssistance: { ...day.transferAssistance, [kind]: null } }
      : {}),
  }));

/** Adds or drops the VIP airport assistance on the arrival or departure. */
export const setTransferAssistance = (
  draft: AdvisorDraft,
  dayId: string,
  kind: 'arrival' | 'departure',
  assistance: PlannerTransferAssistance | null
): AdvisorDraft =>
  updateDay(draft, dayId, (day) => ({
    ...day,
    transferAssistance: { ...day.transferAssistance, [kind]: assistance },
  }));

/** The time of one of the day's transfers (T-425); null: its usual time. */
export const setTransferTime = (
  draft: AdvisorDraft,
  dayId: string,
  kind: PlannerTransferKind,
  time: PlannerTransferTime | null
): AdvisorDraft =>
  updateDay(draft, dayId, (day) => ({
    ...day,
    transferTimes: {
      ...day.transferTimes,
      [kind]: time ? { start: time.start, end: time.end || null } : null,
    },
  }));

const updateDay = (
  draft: AdvisorDraft,
  dayId: string,
  change: (day: AdvisorDraftDay) => AdvisorDraftDay
): AdvisorDraft => ({
  ...draft,
  legs: draft.legs.map((leg) => ({
    ...leg,
    days: leg.days.map((day) => (day.id === dayId ? change(day) : day)),
  })),
});
